import { useState } from "react";

import { Edit as EditIcon } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { ExerciseItem } from "api/services/content/schemas/exercises";
import { PreviewContainer } from "routes/library/components";
import { StyledTags, TopRow } from "routes/library/components/styles";
import { InfoBox } from "routes/library/views/Exercises/components/styles";
import { Counter } from "routes/patients/PatientProfile/components/UserCourse/components/Counter";
import { JointTag } from "routes/patients/PatientProfile/components/UserCourse/components/SharedStyledComponents";
import { parseHTML } from "utils/parseHTML";
import {
  translateExerciseEquipment,
  translateMovement,
  translatePainLocation,
  translatePosition,
  translatePrimaryFunction,
} from "utils/translations";

import type { PreviewTab } from "./ImageAndVideoContainer";

import { DurationInfo, ImageAndVideoContainer } from ".";

interface Props {
  boxShadow?: boolean;
  defaultLevel?: number;
  exercise: Pick<ExerciseItem, "levels" | "primary_function" | "target_joints">;
  isEditable?: boolean;
  isLoading?: boolean;
  previewTab?: PreviewTab;
}

export const ExercisePreview: React.FC<Props> = ({
  boxShadow,
  defaultLevel = 1,
  exercise,
  isEditable,
  isLoading,
  previewTab,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentLevel, setCurrentLevel] = useState(defaultLevel);

  const { levels = [], primary_function, target_joints = [] } = exercise;

  const selectedLevel = levels[currentLevel - 1];
  const {
    about,
    caption_file_url,
    duration_icon,
    duration_unit,
    equipment = [],
    image_url,
    instructions_text1,
    instructions_text3,
    movement,
    position,
    reps,
    sets,
    tip_description,
    tip_title,
    title,
    variation_title,
    video_file_url,
    video_thumbnail_file_url,
  } = selectedLevel;

  const hasMoreThanOneLevel = levels.length > 1;
  const translatedTags = target_joints?.map(tag => translatePainLocation({ painLocation: tag }));
  const translatedEquipment = equipment?.map(equip => translateExerciseEquipment(equip));

  return (
    <PreviewContainer
      showBoxShadow={boxShadow}
      title={`${title} ${variation_title ? `(${variation_title})` : ""}`}
      topContent={
        <>
          {isEditable && (
            <IconButton onClick={() => navigate("edit")} size="large" style={{ marginLeft: "auto" }}>
              <EditIcon />
            </IconButton>
          )}

          {hasMoreThanOneLevel && (
            <TopRow>
              <Counter
                value={currentLevel}
                maxValue={levels.length ?? 1}
                minValue={1}
                clickCallback={newLevel => setCurrentLevel(newLevel)}
              />
            </TopRow>
          )}
        </>
      }
    >
      <Box display="flex" justifyContent="center" flexWrap="wrap" gap={3}>
        <Box maxWidth={650} minWidth={300} flex={2}>
          <ImageAndVideoContainer
            isLoading={isLoading}
            image_url={image_url || ""}
            caption_file_url={caption_file_url || undefined}
            video_file_url={video_file_url || ""}
            video_thumbnail_file_url={video_thumbnail_file_url || ""}
            previewTab={previewTab}
          />
        </Box>
        <Box minWidth={300} flex={1}>
          {primary_function && (
            <InfoBox>
              <Typography variant="h4" mb={1}>
                {t("library.exercises.fields.primary_function")}
              </Typography>
              <Typography variant="body_thin">{translatePrimaryFunction(primary_function)}</Typography>
            </InfoBox>
          )}

          {position && (
            <InfoBox>
              <Typography variant="h4" mb={1}>
                {t("library.exercises.fields.position")}
              </Typography>
              <Typography variant="body_thin">{translatePosition(position)}</Typography>
            </InfoBox>
          )}

          {movement && (
            <InfoBox>
              <Typography variant="h4" mb={1}>
                {t("library.exercises.fields.movement")}
              </Typography>
              <Typography variant="body_thin">{translateMovement(movement)}</Typography>
            </InfoBox>
          )}

          {translatedTags.length > 0 && (
            <InfoBox>
              <Typography variant="h4" mb={1}>
                {t("library.exercises.fields.target_joints")}
              </Typography>
              <StyledTags>
                {translatedTags.map(tag => (
                  <JointTag key={tag} $variant="blue">
                    {tag}
                  </JointTag>
                ))}
              </StyledTags>
            </InfoBox>
          )}

          {translatedEquipment && translatedEquipment.length > 0 && (
            <InfoBox>
              <Typography variant="h4" mb={1}>
                {t("library.exercises.fields.equipment")}
              </Typography>
              <StyledTags>
                {translatedEquipment.map(tag => (
                  <JointTag key={tag} $variant="blue">
                    {tag}
                  </JointTag>
                ))}
              </StyledTags>
            </InfoBox>
          )}

          {about && (
            <InfoBox>
              <Typography variant="h4" mb={1}>
                {t("library.exercises.fields.about")}
              </Typography>
              {/* eslint-disable-next-line react/no-danger */}
              <Typography variant="body_thin" dangerouslySetInnerHTML={{ __html: parseHTML(about) }} />
            </InfoBox>
          )}

          {(instructions_text1 || instructions_text3) && (
            <InfoBox>
              <Typography variant="h4" mb={1}>
                {t("library.exercises.fields.instructions")}
              </Typography>
              {instructions_text1 && (
                <>
                  {/* eslint-disable-next-line react/no-danger */}
                  <Typography variant="body_thin" dangerouslySetInnerHTML={{ __html: parseHTML(instructions_text1) }} />
                </>
              )}
              {instructions_text3 && (
                <>
                  {/* eslint-disable-next-line react/no-danger */}
                  <Typography variant="body_thin" dangerouslySetInnerHTML={{ __html: parseHTML(instructions_text3) }} />
                </>
              )}
            </InfoBox>
          )}

          <InfoBox>
            <DurationInfo duration_icon={duration_icon} duration_unit={duration_unit} reps={reps} sets={sets} />
          </InfoBox>

          {tip_description && (
            <InfoBox>
              <Typography variant="h4" mb={1}>
                {tip_title || t("library.exercises.fields.tip_title_default")}
              </Typography>
              {/* eslint-disable-next-line react/no-danger */}
              <Typography variant="body_thin" dangerouslySetInnerHTML={{ __html: parseHTML(tip_description) }} />
            </InfoBox>
          )}
        </Box>
      </Box>
    </PreviewContainer>
  );
};
